// 외주 의뢰서 확장 — 제조 의뢰(일반 보철·지르코니아·CNC·Metal print·기타) 웹 데이터 층 (2026-10-13 대표 "전부 바로 진행")
// 계획 docs/계획_외주의뢰서확장_2026-10-13.md §1-1·§1-3. 서버 lib/outsource-mf.js · rules outsourceOrders flow 'mf' 와 같은 계약.
//  - outsourceOrders/{id} flow 'mf' 본문: 환자 실명 없음. 실명 = outsourceOrderPatient/{id}(보낸 회사 2년, 업체는 기한 안에서만 rules 가 허용)
//  - 치과 이름(내 메모) = outsourceOrderPrivate/{id} — 업체는 읽지 못한다
//  - STL = Storage outsourceMf/{보낸기공소}/{id}/{n}.stl (원래 파일 이름 저장 안 함). 내려받기는 getOutsourceFileUrl 서명 URL 만
//  - 「확인」 = markOutsourceOpened(받는 회사가 열 때), 「제작 중」 = 첫 내려받기(서버). 클라이언트는 [거절]·[취소]만 쓴다
// 경로·필드는 이 파일 한 곳에서만 정한다. 로그에 환자명 금지.
import { collection, doc, getDoc, getDocs, query, where, writeBatch, updateDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref as sref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase/config';

const REGION = 'asia-northeast3';
const callable = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// 대표 10-25: STL 로 보내고 받는 종류만 연다. id 는 데이터 호환 때문에 유지 — cnc = 커스텀 어버트먼트(의료기기 제조업 게이트·임플란트 시스템 상세 그대로).
//   general(일반 보철)·cad(CAD 디자인)는 목록에서 뺐다(라벨은 옛 주문 표시용으로 남김). 새 id: milling(CNC 밀링), jig(지그).
// 10-25 대표 정정: "CNC 밀링 = 커스텀 어버트먼트" — 별도 id 'milling' 은 목록에서 뺀다(라벨만 남겨 옛 문서 표시). 5종.
export const MF_KINDS = ['metal', 'cnc', 'zirconia', 'jig', 'etc'];
export const MF_KIND_LABEL = { metal: '메탈프린트', cnc: 'CNC 밀링 (커스텀 어버트먼트)', zirconia: '지르코니아 크라운', jig: '지그', etc: '기타', milling: 'CNC 밀링(구)', general: '일반 보철', cad: 'CAD 디자인' };
export const MF_KIND_DESC = {
    metal: '코핑·프레임 구조물 메탈 3D 프린트 (주문 기공소가 최종 완성)',
    zirconia: '풀 지르코니아·레이어드 크라운 (STL 보냄)',
    jig: '임플란트 지그 제작 (STL 보냄)',
    cnc: '커스텀 어버트먼트 CNC 밀링 (의료기기 제조업체 · 임플란트 시스템 지정)',
    etc: '자유 입력 + 파일',
    general: '크라운·브릿지·인레이 등 보철 제작 (종전 종류 — 새 의뢰 불가)',
    cad: 'CAD 디자인 (숨김 — 대표 10-25)',
};
// 칩 색(미리보기 v11 — 종류별 파스텔)
export const MF_KIND_COLOR = {
    general: { fg: '#be123c', bg: '#ffe4e6' }, zirconia: { fg: '#0369a1', bg: '#e0f2fe' }, cnc: { fg: '#3730a3', bg: '#e0e7ff' },
    metal: { fg: '#047857', bg: '#d1fae5' }, cad: { fg: '#334155', bg: '#f1f5f9' }, etc: { fg: '#475569', bg: '#f8fafc' },
    milling: { fg: '#9a3412', bg: '#ffedd5' }, jig: { fg: '#6d28d9', bg: '#ede9fe' },
};
// 상태 4단계 + 거절·취소 — 10-13 3단계에 10-23 「출고」(shipped, 업체 [출고] = 명세서 기준) 추가
export const MF_STATUS = {
    pending: { text: '보냄', recvText: '새 의뢰', fg: '#475569', bg: '#f1f5f9', step: 1 },
    opened: { text: '확인', fg: '#1d4ed8', bg: '#dbeafe', step: 2 },
    in_production: { text: '제작 중', fg: '#b45309', bg: '#fef3c7', step: 3 },
    shipped: { text: '출고', fg: '#047857', bg: '#d1fae5', step: 4 },
    rejected: { text: '거절', fg: '#b91c1c', bg: '#fee2e2', step: 0 },
    cancelled: { text: '취소', fg: '#b91c1c', bg: '#fee2e2', step: 0 },
};
// 종류별 상세 선택지(미리보기 v11 L1e·L1f·L1b·L1c)
export const MF_DETAIL = {
    general: { items: ['크라운', '브릿지', '인레이·온레이', '라미네이트', '포스트 코어'], materials: ['PFM', '골드', '비귀금속', '세라믹'] },
    zirconia: { items: ['풀 지르코니아', '레이어드', '지르코니아 코핑'], materials: ['다층', '단층', '고투명'] },
    cnc: { items: ['커스텀 어버트먼트'], materials: ['티타늄(Gr.5)', '티타늄 프리밀드'], connections: ['Hex', 'Non-hex'] },
    metal: { items: ['코핑', '프레임'], materials: ['CoCr', 'Ti'] },
    milling: { items: ['크라운·브릿지 밀링', '티타늄 바', '임시치아(PMMA)'], materials: ['지르코니아', 'PMMA', '티타늄', '왁스'] },
    jig: { items: ['임플란트 지그'], materials: ['레진(3D 프린트)', 'PMMA 밀링'] },
    etc: { items: [], materials: [] },
};
export const MF_STL_MAX_BYTES = 100 * 1024 * 1024;
export const MF_NOTE_NAME_WARNING = '요청 사항에는 환자 이름을 적지 마세요 (이름은 ③에서만)';

const ordersCol = () => collection(db, 'outsourceOrders');

/** 케이스 번호 MF-yymmdd-### (KST, 같은 날 겹치지 않게 시각+난수) */
export function newMfCaseNo(now = new Date()) {
    const k = new Date(now.getTime() + 9 * 3600 * 1000);
    const ymd = `${String(k.getUTCFullYear()).slice(2)}${String(k.getUTCMonth() + 1).padStart(2, '0')}${String(k.getUTCDate()).padStart(2, '0')}`;
    const seq = String(Math.floor(Math.random() * 9000) + 1000);
    return `MF-${ymd}-${seq}`;
}

/** STL 검사 — .stl 만, 1개 100MB 이하. 반환: 오류 문구 또는 '' */
export function stlFileError(file) {
    if (!file) return '파일이 없습니다';
    if (!/\.stl$/i.test(file.name || '')) return 'STL 파일만 올릴 수 있습니다 (주문서 파일·ZIP 불가)';
    if (file.size <= 0) return '빈 파일입니다';
    if (file.size > MF_STL_MAX_BYTES) return '파일 1개당 100MB 이하만 올릴 수 있습니다';
    return '';
}

/** 치아 — 저장 형식 "14,15,16"(rules 정규식, 보안 검수 하-4) · 화면 배열 둘 다 → 숫자 배열 */
export function teethArray(teeth) {
    if (Array.isArray(teeth)) return teeth.filter((x) => Number.isInteger(x));
    if (typeof teeth === 'string' && teeth) return teeth.split(',').map((x) => parseInt(x, 10)).filter((x) => Number.isInteger(x));
    return [];
}
/** 파일 번호 목록 1..fileCount (원래 파일 이름은 저장하지 않는다) */
export const fileNos = (o) => Array.from({ length: Math.max(0, Number(o && o.fileCount) || 0) }, (_, i) => i + 1);

/** 치아 표기 — 연결이면 #14–#16 처럼 */
export function teethLabel(teeth, bridge) {
    const t = teethArray(teeth).slice().sort((a, b) => a - b);
    if (!t.length) return '';
    if (bridge && t.length > 1) return `${t.map((x) => `#${x}`).join(' ')} 연결`;
    return t.map((x) => `#${x}`).join(' ');
}

/** 상세 요약 칩 문구 */
export function detailSummary(o) {
    const d = o.detail || {};
    if (o.kind === 'cnc') return [d.system, d.connection, d.platform, d.diameter].filter(Boolean).join(' · ');
    return [d.item, d.material].filter(Boolean).join(' · ');
}

// ── 목록 ──────────────────────────────────────────────────────────────
const msOf = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : (v instanceof Date ? v.getTime() : (typeof v === 'number' ? v : null)));
export { msOf as mfMsOf };

export async function listSentMf(companyId) {
    const s = await getDocs(query(ordersCol(), where('fromUserId', '==', companyId)));
    return s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((o) => o.flow === 'mf')
        .sort((a, b) => (msOf(b.createdAt) || 0) - (msOf(a.createdAt) || 0));
}
export async function listReceivedMf(companyId) {
    const s = await getDocs(query(ordersCol(), where('toUserId', '==', companyId)));
    return s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((o) => o.flow === 'mf')
        .sort((a, b) => (msOf(b.createdAt) || 0) - (msOf(a.createdAt) || 0));
}

/** 보낸 쪽: 실명·내 메모(없거나 권한 없으면 null) */
export async function readSenderExtras(orderId) {
    const [p, m] = await Promise.all([
        getDoc(doc(db, 'outsourceOrderPatient', orderId)).catch(() => null),
        getDoc(doc(db, 'outsourceOrderPrivate', orderId)).catch(() => null),
    ]);
    return { patient: p && p.exists() ? p.data() : null, memo: m && m.exists() ? (m.data().memo || '') : '' };
}
/** 받는 쪽: 실명(기한·CNC 차단이면 rules 거부 → null) */
export async function readVendorPatientName(orderId) {
    try {
        const p = await getDoc(doc(db, 'outsourceOrderPatient', orderId));
        return p.exists() ? (p.data().patientName || null) : null;
    } catch (e) {
        if (e && e.code === 'permission-denied') return null;   // 기한 지남·CNC — 케이스 번호만(정상)
        throw e;
    }
}

// ── 생성 ──────────────────────────────────────────────────────────────
/**
 * 의뢰 1건 생성 = 본문·실명·내 메모 한 배치(rules: 같은 배치만 실명 생성 허용) → STL 올리기.
 * @param {object} c {kind, vendor:{id,name}, patientName, memo, teeth, bridge, detail, dueDate, note, files:File[], caseNo}
 * @param {object} me {companyId, uid, name}
 * @param {(done:number,total:number)=>void} onProgress
 * @returns {Promise<{id:string, uploaded:number, failed:number}>}
 */
export async function createMfOrder(c, me, onProgress) {
    const files = c.files || [];
    if (!files.length) throw Object.assign(new Error('STL 파일을 1개 이상 올려 주세요'), { code: 'no-files' });
    const ref = doc(ordersCol());
    const b = writeBatch(db);
    // 보안 검수 하-4(10-13): 원래 파일 이름·크기 배열을 저장하지 않는다(fileCount 만), 치아는 "14,15,16" 문자열, 상세는 정해진 키만
    const d = c.detail || {};
    const str = (v, n) => String(v || '').slice(0, n);
    const detail = {
        item: str(d.item, 40), material: str(d.material, 40), shade: str(d.shade, 20), system: str(d.system, 40), platform: str(d.platform, 20),
        connection: str(d.connection, 20), diameter: str(d.diameter, 10), anodizing: !!d.anodizing, anodizingColor: str(d.anodizingColor, 10), postProcess: str(d.postProcess, 40),
        qty: Number.isInteger(d.qty) && d.qty >= 1 && d.qty <= 64 ? d.qty : null,
    };
    const order = {
        flow: 'mf', kind: c.kind, fromUserId: me.companyId, fromName: str(me.name, 60), toUserId: c.vendor.id, toName: str(c.vendor.name, 60),
        participants: [me.companyId, c.vendor.id], caseNo: c.caseNo,
        teeth: teethArray(c.teeth).slice().sort((a, b) => a - b).join(','), bridge: !!c.bridge, detail, dueDate: c.dueDate || '', note: (c.note || '').slice(0, 500),
        fileCount: files.length,
        status: 'pending', statusChangedAt: serverTimestamp(), createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: me.uid,
    };
    b.set(ref, order);
    b.set(doc(db, 'outsourceOrderPatient', ref.id), {
        patientName: String(c.patientName || '').trim().slice(0, 40), hasName: true,
        fromUserId: me.companyId, toUserId: c.vendor.id, kind: c.kind, createdAt: serverTimestamp(),
    });
    if (c.memo) b.set(doc(db, 'outsourceOrderPrivate', ref.id), { fromUserId: me.companyId, memo: String(c.memo).slice(0, 100), updatedAt: serverTimestamp() });
    await b.commit();
    let uploaded = 0; let failed = 0;
    for (let i = 0; i < files.length; i += 1) {
        try {
            await uploadBytes(sref(storage, `outsourceMf/${me.companyId}/${ref.id}/${i + 1}.stl`), files[i], { contentType: 'model/stl' });
            uploaded += 1;
        } catch (e) {
            failed += 1;
            console.error('[외주 의뢰] STL 올리기 실패 order=%s n=%d code=%s', ref.id, i + 1, e && e.code);
        }
        if (onProgress) onProgress(i + 1, files.length);
    }
    return { id: ref.id, uploaded, failed };
}

/**
 * PC 에이전트가 STL 을 올리는 의뢰 1건 생성(외주 의뢰 페이지, 2026-10-30 대표 "파일 올릴 필요 없이 보내기").
 * 본문·실명·내 메모를 한 배치로 쓰고(createMfOrder 와 같은 필드 규칙) filesFrom 'agent' 를 단다 — STL 은 에이전트가 outsourceMf/… 에 올리고
 * markOutsourceFilesReady 로 알린다(그때 업체 알림). 웹은 파일을 올리지 않는다.
 * @param {object} c {kind, vendor:{id,name}, patientName, memo, teeth, bridge, detail, dueDate, note, fileCount, caseNo}
 * @returns {Promise<{id:string}>}
 */
export async function createAgentMfOrder(c, me) {
    const fileCount = Number(c.fileCount) || 0;
    if (fileCount < 1 || fileCount > 100) throw Object.assign(new Error('보낼 STL이 없습니다'), { code: 'no-files' });
    const ref = doc(ordersCol());
    const b = writeBatch(db);
    const d = c.detail || {};
    const str = (v, n) => String(v || '').slice(0, n);
    const detail = {
        item: str(d.item, 40), material: str(d.material, 40), shade: '', system: str(d.system, 40), platform: str(d.platform, 20),
        connection: str(d.connection, 20), diameter: str(d.diameter, 10), anodizing: false, anodizingColor: '', postProcess: '',
        qty: Number.isInteger(d.qty) && d.qty >= 1 && d.qty <= 64 ? d.qty : null,
    };
    b.set(ref, {
        flow: 'mf', kind: c.kind, fromUserId: me.companyId, fromName: str(me.name, 60), toUserId: c.vendor.id, toName: str(c.vendor.name, 60),
        participants: [me.companyId, c.vendor.id], caseNo: c.caseNo,
        teeth: teethArray(c.teeth).slice().sort((a, b2) => a - b2).join(','), bridge: !!c.bridge, detail, dueDate: c.dueDate || '', note: (c.note || '').slice(0, 500),
        fileCount, filesFrom: 'agent',
        status: 'pending', statusChangedAt: serverTimestamp(), createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: me.uid,
    });
    b.set(doc(db, 'outsourceOrderPatient', ref.id), {
        patientName: String(c.patientName || '').trim().slice(0, 40) || c.caseNo, hasName: true,
        fromUserId: me.companyId, toUserId: c.vendor.id, kind: c.kind, createdAt: serverTimestamp(),
    });
    if (c.memo) b.set(doc(db, 'outsourceOrderPrivate', ref.id), { fromUserId: me.companyId, memo: String(c.memo).slice(0, 100), updatedAt: serverTimestamp() });
    await b.commit();
    return { id: ref.id };
}

// ── 전이 ──────────────────────────────────────────────────────────────
export async function rejectMf(orderId, reason, uid) {
    const r = String(reason || '').trim();
    if (!r) throw Object.assign(new Error('거절 사유를 적어 주세요'), { code: 'reason-required' });
    await updateDoc(doc(db, 'outsourceOrders', orderId), {
        status: 'rejected', rejectReason: r.slice(0, 300), rejectedAt: serverTimestamp(), rejectedBy: uid,
        statusChangedAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
}
export async function cancelMf(orderId, reason, uid) {
    await updateDoc(doc(db, 'outsourceOrders', orderId), {
        status: 'cancelled', cancelReason: String(reason || '').trim().slice(0, 300), cancelledAt: serverTimestamp(), cancelledBy: uid,
        statusChangedAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
}
export const markMfOpened = (orderId) => callable('markOutsourceOpened')({ orderId }).then((r) => r.data);
export const getMfFileUrl = (orderId, n) => callable('getOutsourceFileUrl')({ orderId, n }).then((r) => r.data);
export const eraseMfPatientName = (orderId) => callable('eraseOutsourcePatientName')({ orderId }).then((r) => r.data);
/** 출고 — 받은 업체(대표·직원). 제작 중에서만, 송장번호·블랭크 LOT 선택 (10-23) */
export const markMfShipped = (orderId, trackingNo = '', blankLot = '') => callable('markOutsourceShipped')({ orderId, trackingNo, blankLot }).then((r) => r.data);
export const buildMfStatement = (labId, month) => callable('buildOutsourceMfStatement')({ labId, month }).then((r) => r.data);
/** 외주 거래명세서 화면(10-31): 기간·목록에서 고른 출고 건으로 — 서버가 출고·미청구 확인 */
export const buildMfStatementPicked = (labId, orderIds) => callable('buildOutsourceMfStatement')({ labId, orderIds }).then((r) => r.data);

// ── 업체·연결·단가표 ───────────────────────────────────────────────────
export async function listApprovedVendors() {
    const s = await getDocs(query(collection(db, 'vendorProfiles'), where('status', '==', 'approved')));
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function getVendorProfile(uid) {
    const s = await getDoc(doc(db, 'vendorProfiles', uid));
    return s.exists() ? { id: s.id, ...s.data() } : null;
}
/**
 * 업체 프로필 저장. 보안 검수 중-2(10-13): 받는 종류(kinds)가 바뀌거나 반려 상태면 「재신청」(status review — 관리자 재승인 전 찾기·새 의뢰에서 빠짐),
 * 아니면 소개 필드만 고친다(rules vendorEditable).
 * @param {object|null} prev 기존 프로필(없으면 새 신청)
 * @param {object} [opts] { reapply?: boolean } — 보류 중 서류를 올렸을 때 appliedAt 갱신(재심사 목록 노출)
 */
export async function saveVendorProfile(uid, data, prev, opts = {}) {
    const ref = doc(db, 'vendorProfiles', uid);
    if (!prev) return setDoc(ref, { ...data, status: 'review', appliedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    const { kinds, vendorType, status, ...editable } = data;
    const sameKinds = JSON.stringify([...(prev.kinds || [])].sort()) === JSON.stringify([...(kinds || [])].sort());
    // S5(10-27): 'ended'(본인 종료) 뒤 다시 시작·보류 중 서류 올림(opts.reapply) 도 재신청 — appliedAt 갱신으로 재심사 목록에 뜬다(rules 두 번째 update 절)
    if (sameKinds && !['rejected', 'ended'].includes(prev.status) && !opts.reapply) return updateDoc(ref, { ...editable, updatedAt: serverTimestamp() });
    return updateDoc(ref, { ...editable, kinds, status: 'review', appliedAt: serverTimestamp(), updatedAt: serverTimestamp() });
}
/** 심사 기록(반려 사유) — 본인·관리자만 읽는 vendorReviews/{uid} */
export async function getVendorReview(uid) {
    try { const s = await getDoc(doc(db, 'vendorReviews', uid)); return s.exists() ? s.data() : null; }
    catch (e) { if (e && e.code === 'permission-denied') return null; throw e; }
}
/** 치과기공사 면허 번호·성명 — 공개 프로필이 아니라 본인·관리자만 읽는 서류 메타에 둔다 */
export async function saveVendorLicense(uid, licenseNo, licenseName) {
    await setDoc(doc(collection(db, 'vendorDocs', uid, 'files')), { type: 'license', licenseNo: String(licenseNo || '').slice(0, 20), licenseName: String(licenseName || '').slice(0, 20), uploadedAt: serverTimestamp() });
}
export async function uploadVendorDoc(uid, type, file) {
    const safe = `${type}_${Date.now()}.${(file.name.split('.').pop() || 'pdf').toLowerCase().replace(/[^a-z0-9]/g, '')}`;
    const path = `vendorDocs/${uid}/${safe}`;
    await uploadBytes(sref(storage, path), file, { contentType: file.type });
    await setDoc(doc(collection(db, 'vendorDocs', uid, 'files')), { type, path, size: file.size, uploadedAt: serverTimestamp() });
    return path;
}
/** 내 기공소가 건 연결(labId == 나) */
export async function listMyLinksAsLab(companyId) {
    const s = await getDocs(query(collection(db, 'vendorLinks'), where('labId', '==', companyId)));
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** 나에게 온 연결(vendorId == 나) */
export async function listMyLinksAsVendor(companyId) {
    const s = await getDocs(query(collection(db, 'vendorLinks'), where('vendorId', '==', companyId)));
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function requestVendorLink(me, vendor, kinds, message, existing) {
    const id = `${me.companyId}_${vendor.id}`;
    const ref = doc(db, 'vendorLinks', id);
    if (existing && ['declined', 'ended'].includes(existing.status)) {
        return updateDoc(ref, { status: 'requested', kinds, message: String(message || '').slice(0, 300), requestedAt: serverTimestamp(), requestedBy: me.uid, updatedAt: serverTimestamp() });
    }
    return setDoc(ref, {
        labId: me.companyId, labName: me.name || '', vendorId: vendor.id, vendorName: vendor.businessName || '',
        kinds, message: String(message || '').slice(0, 300), status: 'requested', requestedAt: serverTimestamp(), requestedBy: me.uid, updatedAt: serverTimestamp(),
    });
}
export const respondVendorLink = (linkId, accept, uid) => updateDoc(doc(db, 'vendorLinks', linkId), { status: accept ? 'accepted' : 'declined', respondedAt: serverTimestamp(), respondedBy: uid, updatedAt: serverTimestamp() });
export const endVendorLink = (linkId, uid) => updateDoc(doc(db, 'vendorLinks', linkId), { status: 'ended', endedAt: serverTimestamp(), endedBy: uid, updatedAt: serverTimestamp() });
export async function readVendorPriceList(vendorId) {
    try { const s = await getDoc(doc(db, 'vendorPriceLists', vendorId)); return s.exists() ? (s.data().items || []) : []; }
    catch (e) { if (e && e.code === 'permission-denied') return null; throw e; }
}
export const saveVendorPriceList = (uid, items) => setDoc(doc(db, 'vendorPriceLists', uid), { items: items.slice(0, 200), updatedAt: serverTimestamp() });

// ── 관리자 ────────────────────────────────────────────────────────────
export async function listVendorProfilesForReview() {
    const s = await getDocs(collection(db, 'vendorProfiles'));
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function listVendorDocs(uid) {
    const s = await getDocs(collection(db, 'vendorDocs', uid, 'files'));
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** 관리자 심사 — 프로필에는 상태만(공개), 반려 사유·심사자는 비공개 vendorReviews 에(보안 검수 하-1) */
/**
 * @param {object} [extra] { cncApproved?: boolean, gmpExpireAt?: Date|null } — 기공소 CNC 제조 허가 서류 확인·GMP 만료일(관리자만, rules)
 */
export async function reviewVendorProfile(vendorId, status, adminUid, reason, extra = {}) {
    const b = writeBatch(db);
    const upd = { status, updatedAt: serverTimestamp() };
    if (typeof extra.cncApproved === 'boolean') upd.cncApproved = extra.cncApproved;
    if (extra.gmpExpireAt instanceof Date) upd.gmpExpireAt = Timestamp.fromDate(extra.gmpExpireAt);
    b.update(doc(db, 'vendorProfiles', vendorId), upd);
    b.set(doc(db, 'vendorReviews', vendorId), { status, rejectReason: status === 'rejected' ? String(reason || '').slice(0, 300) : '', reviewedBy: adminUid, reviewedAt: serverTimestamp() });
    await b.commit();
}


// ── 가입 위주 외주 업체 등록 S5 (2026-10-27, 계획 docs/계획_가입위주_외주업체등록_S5_2026-10-27.md §2-C) ──
/** 업체 프로필 상태 문구 — 'ended' = 본인이 외주 수주 종료(번호 파기) */
export const VENDOR_STATUS_TEXT = { review: '검토 중', approved: '승인됨', rejected: '반려', expired: '만료', ended: '종료' };
/**
 * 관리자 가입 승인·거절 1회 호출(서버 lib/signup-approval.js) — users 승인 + vendorProfiles/vendorReviews + 알림(서버가 보냄).
 * @param {object} payload { uid, decision:'approved'|'rejected', rejectReason?, vendor?: { grant, hold?, holdReason?, kinds?, gmpExpireAt?: ISO|null, cncApproved? } }
 *   이미 approved 인 회원에 vendor.grant 만 보내면 vendorOnly 모드(보류 해제·누락 보정). 실패는 HttpsError 메시지 그대로(번호 문자열 없음).
 */
export const adminApproveSignup = (payload) => callable('adminApproveSignup')(payload).then((r) => r.data);
/** 외주 수주 종료(번호 파기) — 본인 대표 계정. vendorDocs 번호 메타 삭제 + vendorProfiles status 'ended' (법무 §2-6 ③(b)) */
export const endVendorIntake = () => callable('endVendorIntake')({}).then((r) => r.data);

// ── 외주 지그 제조사 제작분 (대표 10-30) — vendorJigUsage/{vendorId}_{YYYYMM}, 서버(completeJigJob)만 씀 ──
/** 이번 달(KST) 'YYYY-MM' */
export function kstMonth(ms = Date.now()) { return new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 7); }
/** 제조사 자신의 달별 지그 제작 수 — 최근 달 먼저 */
export async function listVendorJigUsage(vendorId) {
    const s = await getDocs(query(collection(db, 'vendorJigUsage'), where('vendorId', '==', vendorId)));
    return s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.month).localeCompare(String(a.month)));
}
/** 관리자 — 한 달의 제조사별 지그 제작 수 { vendorId: count } */
export async function vendorJigCountsOfMonth(month = kstMonth()) {
    const s = await getDocs(query(collection(db, 'vendorJigUsage'), where('month', '==', month)));
    const out = {};
    s.docs.forEach((d) => { const x = d.data(); out[x.vendorId] = Number(x.count) || 0; });
    return out;
}
